// Story 8.5 — the payout-request confirm modal.
//
// STORY 1.37 IS THE SPEC FOR WHAT IT SHOWS: gross, fee, net and honest delivery
// timing, all before the creator confirms. An undisclosed fee is simply
// uncollectable (§ 118, stk. 3), and the fee here is FLAT and at cost — so the
// modal states the cents figure and says what it is, rather than hiding a
// percentage behind a total.
//
// COPY DISCIPLINE (UX-DR49). This is a supplier being paid, not a user cashing
// out. No string in this file says "withdraw", "cash out" or "your money". It
// says "payout", "revenue share" and "invoice", which are the words that belong
// to the earned pot — and it says plainly that the money arrives at the
// settlement cut-off, not now, because pretending otherwise would make the
// product describe a withdrawal while the paperwork describes a trade payable.

import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@greenhold/components";
import toast from "react-hot-toast";

import { useRequestPayout, type RequestPayoutError } from "./useRequestPayout";
import type { CreatorPayoutOptions, PayoutRail } from "./usePayoutOptions";

function formatEur(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  return `${sign}€${Math.floor(abs / 100)}.${(abs % 100)
    .toString()
    .padStart(2, "0")}`;
}

function parseEurToCents(input: string): number | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const num = Number(trimmed.replace(",", "."));
  if (!Number.isFinite(num) || num <= 0) return null;
  return Math.round(num * 100);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const RAIL_LABEL: Record<PayoutRail, string> = {
  bank: "Bank transfer",
  card: "To your card",
};

type RequestPayoutModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  options: CreatorPayoutOptions;
};

export function RequestPayoutModal({
  open,
  onOpenChange,
  options,
}: RequestPayoutModalProps) {
  const [amountInput, setAmountInput] = useState("");
  const [rail, setRail] = useState<PayoutRail>("bank");
  const [inlineError, setInlineError] = useState<string | null>(null);
  const request = useRequestPayout();
  // Depend on the stable `reset` fn, not the whole mutation object: a new
  // object identity every render + reset() notifying the observer =
  // "Maximum update depth".
  const { reset: resetRequest } = request;

  useEffect(() => {
    if (!open) {
      setAmountInput("");
      setInlineError(null);
      resetRequest();
    }
  }, [open, resetRequest]);

  useEffect(() => {
    if (open) {
      setAmountInput((options.earnings_cents / 100).toFixed(2));
      setRail(options.rails[0]?.rail ?? "bank");
    }
  }, [open, options.earnings_cents, options.rails]);

  const selectedRail =
    options.rails.find((r) => r.rail === rail) ?? options.rails[0];
  const amountCents = parseEurToCents(amountInput);
  const feeCents = selectedRail?.fee_cents ?? 0;
  const netCents = amountCents === null ? null : amountCents - feeCents;

  function submit() {
    setInlineError(null);
    if (amountCents === null) {
      setInlineError("Enter an amount.");
      return;
    }
    if (amountCents > options.earnings_cents) {
      setInlineError(
        `That is more than your earnings (${formatEur(options.earnings_cents)}).`,
      );
      return;
    }
    if (amountCents < options.minimum_cents) {
      setInlineError(
        `The minimum for one settlement is ${formatEur(options.minimum_cents)}. ` +
          `Below that, your earnings roll into the next period — or move them ` +
          `to your Greenhold balance now, free.`,
      );
      return;
    }
    request.mutate(
      { amountCents, rail, idempotencyKey: crypto.randomUUID() },
      {
        onSuccess: (settlement) => {
          toast.success(
            `Invoice ${settlement.invoice_number} raised. We'll pay ` +
              `${formatEur(settlement.net_cents)} at the settlement on ` +
              `${formatDate(settlement.period_end)}.`,
          );
          onOpenChange(false);
        },
        onError: (err: RequestPayoutError) => {
          setInlineError(err.message);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request a payout</DialogTitle>
          <DialogDescription>
            We'll raise an invoice for you now and pay it at the next
            settlement, on {formatDate(options.period.end)}. The amount leaves
            your earnings straight away, so it stops being spendable here.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="payout-amount">Amount from your earnings</Label>
            <Input
              id="payout-amount"
              inputMode="decimal"
              value={amountInput}
              onChange={(e) => setAmountInput(e.target.value)}
            />
            <p className="mt-1 text-xs opacity-70">
              You have {formatEur(options.earnings_cents)} in earnings.
              Minimum per settlement: {formatEur(options.minimum_cents)}.
            </p>
          </div>

          {options.rails.length > 1 ? (
            <div>
              <Label htmlFor="payout-rail">How we pay you</Label>
              <select
                id="payout-rail"
                className="w-full rounded-md border px-3 py-2 bg-transparent"
                value={rail}
                onChange={(e) => setRail(e.target.value as PayoutRail)}
              >
                {options.rails.map((r) => (
                  <option key={r.rail} value={r.rail}>
                    {RAIL_LABEL[r.rail]} — fee {formatEur(r.fee_cents)}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {/* Story 1.37 AC2/AC3 — gross, fee, net and honest timing, before
              anything is confirmed. Real numbers off the server, not UI copy. */}
          <dl className="rounded-md border p-3 text-sm space-y-1">
            <div className="flex justify-between">
              <dt>From your earnings</dt>
              <dd>{amountCents === null ? "—" : formatEur(amountCents)}</dd>
            </div>
            <div className="flex justify-between">
              <dt>
                Payout fee ({selectedRail ? RAIL_LABEL[selectedRail.rail] : "—"})
              </dt>
              <dd>-{formatEur(feeCents)}</dd>
            </div>
            <div className="flex justify-between font-semibold">
              <dt>You receive</dt>
              <dd>{netCents === null ? "—" : formatEur(netCents)}</dd>
            </div>
          </dl>
          <p className="text-xs opacity-70">
            The fee is flat and equal to what it costs us to send the payment —
            it does not grow with the amount.{" "}
            {selectedRail ? `Arrives ${selectedRail.expected_days}.` : null}{" "}
            {options.destination?.last4
              ? `Paid to your destination ending ${options.destination.last4}.`
              : null}
          </p>
          <p className="text-xs opacity-70">
            Prefer to spend it here instead? Moving earnings to your Greenhold
            balance is free and has no minimum.
          </p>

          {inlineError ? (
            <p className="text-sm text-destructive">{inlineError}</p>
          ) : null}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={request.isPending}>
            {request.isPending ? "Raising invoice…" : "Request payout"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
